import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MenuProps } from 'antd';
import { Dropdown, Modal } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useDeleteComment } from 'api/comments/use-delete-comment.ts';
import type { TCommentRes } from 'api/swagger';
import { UserRole, withRoleGuard } from 'shared/hocs/with-role-guard.tsx';
import type { TDropdownChangeInfo } from 'types/dropdown-change-info';

export const CommentControlComponent: React.FC<{ comment: TCommentRes }> = ({ comment }) => {
  const { t } = useTranslation();

  const [modal, contextHolder] = Modal.useModal();
  const [isActiveControl, setIsActiveControl] = useState(false);
  const { mutateAsync: deleteCommentApi } = useDeleteComment();

  const deactivateWithTimeout = () => {
    setTimeout(() => setIsActiveControl(false), 300);
  };

  const confirm = () => {
    modal.confirm({
      title: t('common:confirm'),
      icon: <ExclamationCircleOutlined />,
      content: t('common:delete_confirm_text'),
      okText: t('common:delete'),
      cancelText: t('common:cancel'),
      onCancel: () => deactivateWithTimeout(),
      onOk: async () => {
        await deleteCommentApi({ commentId: comment.id });
        deactivateWithTimeout();
      },
    });
  };

  const handleDropdownClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'delete') {
      confirm();
    }
  };

  const handleDropdownOpenChange = (value: boolean, info: TDropdownChangeInfo) => {
    if (info.source === 'trigger') {
      setIsActiveControl(value);
    }
  };

  return (
    <>
      <Dropdown
        key='more'
        trigger={['click']}
        menu={{
          items: [
            {
              key: 'delete',
              label: t('common:delete'),
              icon: <DeleteOutlined />,
            },
          ],
          onClick: handleDropdownClick,
        }}
        placement='bottomLeft'
        onOpenChange={handleDropdownOpenChange}>
        <MoreOutlined style={{ fontSize: 20 }} />
      </Dropdown>
      {isActiveControl ? contextHolder : null}
    </>
  );
};

export const CommentControl = withRoleGuard(CommentControlComponent, [
  UserRole.admin,
  UserRole.assistant,
  UserRole.customer_manager,
  UserRole.transport_manager,
  UserRole.storekeeper,
  UserRole.accountant,
]);
